import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export type LocaleSelectionMap = Record<string, string>; // userId -> locale

// Cookie-backed storage so the value is readable server-side (enabling zero-flash
// redirects in [locale]/layout.tsx before any HTML is sent to the browser).
// The JSON value is URL-encoded to safely carry `"`, `{`, `}` in the cookie.
const cookieStorage = createJSONStorage<LocaleSelectionMap>(() => ({
  getItem: (key: string): string | null => {
    if (typeof document === 'undefined') return null;
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof document === 'undefined') return;
    const oneYear = 60 * 60 * 24 * 365;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${oneYear}; SameSite=Lax${secure}`;
  },
  removeItem: (key: string): void => {
    if (typeof document === 'undefined') return;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax${secure}`;
  },
}));

// Persist language selection decision across sessions.
// Structured as { [userId]: locale }, e.g. { "user-123": "es-US" }
export const localeSelectionAtom = atomWithStorage<LocaleSelectionMap>('locale-user-preference', {}, cookieStorage, {
  getOnInit: true,
});
